<template>
  <div class="d-flex flex-wrap justify-center">
    <SiteSelector v-model="selectedSite" />
    <v-btn-nd
      v-if="authorizationStore.hasCompanyScoped(Permission.Device_OrderDevice)"
      :disabled="!selectedSite"
      :to="`/request-new-device?siteId=${selectedSite}`"
      class="ma-3"
    >
      {{ t("devices.list.orderDevicesButton") }}
    </v-btn-nd>
    <v-btn-nd
      v-if="authorizationStore.hasAnySiteScoped(Permission.Device_AssignDevice)"
      :disabled="!selectedSite"
      :to="`/assign-new-device?selectedSite=${selectedSite}`"
      class="ma-3"
    >
      {{ t("devices.list.assignDeviceButton") }}
    </v-btn-nd>
  </div>

  <h2 v-if="!selectedSite" class="text-center text-gray">
    {{ t("devices.list.selectSitePrompt") }}
  </h2>

  <div v-if="selectedSite">
    <v-data-table
      :headers="headers"
      :items="items"
      :search="search"
      :loading="devicesLoading"
      :loading-text="t('devices.list.loading')"
      :hide-default-footer="true"
      :items-per-page="itemsPerPage"
      :mobile="null"
      :mobile-breakpoint="800"
      :no-data-text="
        hasItemsWithoutSearch
          ? t('devices.list.noDevicesMatch')
          : t('devices.list.noDevicesAllocated')
      "
    >
      <template #top>
        <v-text-field v-model="search" :label="t('devices.list.searchLabel')" />
      </template>

      <template #item.deviceSlogan="{ item }">
        <EditDeviceSlogan
          :site-id="selectedSite"
          :device-id="item.deviceId!"
          :slogan="item.deviceSlogan"
        />
      </template>

      <template #item.lastTransactionAt="{ value }">
        <v-tooltip v-if="value" activator="parent" location="top">
          {{ timeSinceDate(value) }}
        </v-tooltip>
        {{ formatDate(value) }}
      </template>

      <template #item.lastActivityAt="{ value }">
        <v-tooltip v-if="value" activator="parent" location="top">
          {{ timeSinceDate(value) }}
        </v-tooltip>
        {{ formatDate(value) }}
      </template>

      <template #item.firmwareVersion="{ value }">
        {{ value.replace("Castles_Android", "") }}
      </template>

      <template #item.statusColor="{ value }">
        <v-tooltip activator="parent" location="top">
          {{ getStatusTooltipText(value) }}
        </v-tooltip>
        <v-badge dot inline :color="value || 'white'" />
      </template>

      <template #bottom />
    </v-data-table>
    <v-pagination v-model="page" color="secondary" :length="pageCount" />
  </div>

  <v-alert v-if="thereWasAProblem" color="red lighten-2" dark>
    {{ t("devices.list.errorMessage") }}
  </v-alert>
</template>

<script setup lang="ts">
import { formatRelative, intervalToDuration, format } from "date-fns";
import { inject, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { Device } from "@/api/merchant-service/device";
import { API } from "@/plugins/api";
import { Permission, useAuthorizationStore } from "@/store/authorization";

const { t } = useI18n();
const api = inject<API>("api");
const route = useRoute();
const authorizationStore = useAuthorizationStore();

const thereWasAProblem = ref(false);
const selectedSite = ref<string | undefined>(undefined);
const page = ref(-1);
const pageCount = ref(0);
const itemsPerPage = ref(10);
const items = ref<EnrichedDevice[]>([]);
const hasItemsWithoutSearch = ref(false);
const devicesLoading = ref(false);
const search = ref("");
const headers = ref([
  {
    title: "",
    value: "statusColor",
  },
  {
    title: t("devices.list.headers.serial"),
    value: "deviceId",
  },
  {
    title: t("devices.list.headers.label"),
    value: "deviceSlogan",
    width: "200px",
  },
  {
    title: t("devices.list.headers.model"),
    value: "model",
  },
  {
    title: t("devices.list.headers.lastTransaction"),
    value: "lastTransactionAt",
  },
  {
    title: t("devices.list.headers.lastActivity"),
    value: "lastActivityAt",
  },
  {
    title: t("devices.list.headers.firmwareVersion"),
    value: "firmwareVersion",
  },
]);

function getStatusTooltipText(color: string) {
  switch (color) {
    case "green":
      return t("devices.list.status.last24h");
    case "orange":
      return t("devices.list.status.lastWeek");
    case "red":
      return t("devices.list.status.overWeek");
    default:
      return t("devices.list.status.unknown");
  }
}
function getColor(dateTime: string) {
  if (!dateTime) return "red";

  const dateSince = intervalToDuration({ start: new Date(dateTime), end: new Date() });
  if (!dateSince.days) return "green";
  if (dateSince.days! > 7) return "orange";
  return "red";
}

function formatDate(date: string) {
  if (!date) return "Never";
  return format(new Date(date), "yyy.MM.dd HH:mm");
}

type EnrichedDevice = Device & {
  deviceSlogan?: string;
  statusColor?: string;
};

onMounted(() => {
  if (route.query.selectedSite) {
    selectedSite.value = route.query.selectedSite as string;
  }
});

function timeSinceDate(dateTime: string) {
  if (!dateTime) return "Never";
  const dateSince = intervalToDuration({ start: new Date(dateTime), end: new Date() });
  if (dateSince.days! > 7) {
    return `${dateSince.days!} day${dateSince.days! > 1 ? "s" : ""} ago`;
  }
  return formatRelative(new Date(dateTime), new Date());
}

const loadDevices = async (newPage: number) => {
  devicesLoading.value = true;
  try {
    const result = await api!.device.listDevices(selectedSite.value!, newPage);
    items.value = result.items ?? [];
    pageCount.value = result.totalPages;
    await fetchLoadedDeviceDetails();
  } catch (error) {
    console.error(error);
    thereWasAProblem.value = true;
  } finally {
    devicesLoading.value = false;
  }
};

const fetchLoadedDeviceDetails = async () => {
  items.value.forEach(async (item) => {
    const deviceDetails = await api!.device.fetchDeviceConfig(selectedSite.value!, item.deviceId!);
    const index = items.value.findIndex((i) => i.deviceId === deviceDetails.id);
    items.value[index].deviceSlogan =
      deviceDetails.signature?.deviceSlogan || deviceDetails.id!.slice(-4);
    items.value[index].statusColor = getColor(item.lastActivityAt!);
  });
};

watch(selectedSite, async (newSelectedSite) => {
  let queryParams = "";
  if (newSelectedSite && newSelectedSite !== "null") {
    queryParams = "?selectedSite=" + newSelectedSite;
    page.value = 1;
    search.value = "";
    await loadDevices(1);
    hasItemsWithoutSearch.value = items.value.length > 0;
  }
  history.pushState({}, "", route.path + queryParams);
});
watch(page, async (newPage) => {
  await loadDevices(newPage);
});
watch(search, async (newSearch: string) => {
  devicesLoading.value = true;
  try {
    const result = await api!.device.listDevices(selectedSite.value!, 1, newSearch);
    items.value = result.items ?? [];
    pageCount.value = result.totalPages;
    await fetchLoadedDeviceDetails();
  } catch (error) {
    console.error(error);
    thereWasAProblem.value = true;
  } finally {
    devicesLoading.value = false;
  }
});
</script>

<style scoped>
.v-table :deep(div.v-data-table__td-sort-select) {
  display: none;
}
</style>
